/**
 * `attackRange` — the geometry of an attack's reach and its AoE templates (Blast / Burst /
 * Line / Cone), so that *what the red template draws is exactly who gets resolved*.
 *
 * Two families live here. The **square-only** builders compose the square primitives from
 * `@lancer-vtt/space` (`chebyshevDisk`/`ray`/`cone`/`squareFootprint`): `attackReachCells`
 * (reach wash), `attackAffectedCells` (hovered template), `attackAffectedCombatants` (the
 * template's combatant set). These are the **parity oracle** — neither the resolver nor the
 * overlay calls them in production; they anchor the tests that prove the port geometry matches
 * the square shapes cell-for-cell.
 *
 * The **grid-agnostic port twins** (`attackReachKeys`,
 * `attackAffectedKeys`, `combatantInReachKeys`) draw from the encounter's `worldFor(state)`
 * spatial port (`disk`/`linePattern`/`conePattern`) — the SAME seam the resolver fans out
 * over (`resolveAreaAttack` on `enumerateArea`). These are what the COMBAT.app overlay reads,
 * so its reach wash + AoE template + who's-hit dimming stay honest on hex as well as square.
 * On the live 2d-square surface the twins emit the identical `"x,y"` keys the square builders
 * do (the port's square patterns delegate to the same primitives, `world.key` == `squareKey`).
 * Unplaced combatants contribute nothing (the helpers degrade to the bare targeted cell).
 */
import { type Square, type World, chebyshevDisk, cone, ray, square, squareFootprint, squareKey } from "@lancer-vtt/space";
import type { Cell } from "./types";
import type { Combatant, CombatantId, EncounterState, RangeKind, WeaponRange } from "./types";
import { squareCell } from "./types";
import { terrainVisibility } from "./terrain";
import { worldFor } from "./world";
import { sideOf } from "./model/capabilities";

/** Read a placed combatant's anchor as a `Square`, or null if unplaced / on a non-square grid. */
export function squareAnchor(c: Combatant): Square | null {
  const pos = c.pos;
  if (!pos) return null;
  if ("grid" in pos && pos.grid === "hex") return null;
  return square((pos as { x: number }).x, (pos as { y: number }).y);
}

/** Read a tagged map {@link Cell} as a `Square`, or null if it's a hex cell. */
export function cellAsSquare(cell: Cell): Square | null {
  if ("grid" in cell && cell.grid === "hex") return null;
  return square((cell as { x: number }).x, (cell as { y: number }).y);
}

const REACH_KINDS: RangeKind[] = ["range", "threat"];
const PROJECT_KINDS: RangeKind[] = ["line", "cone"];
const AREA_KINDS: RangeKind[] = ["blast", "burst"];

function maxVal(range: WeaponRange[], kinds: RangeKind[]): number {
  let m = 0;
  for (const r of range) if (kinds.includes(r.kind) && r.val > m) m = r.val;
  return m;
}

/**
 * The weapon's **primary targeting reach** in spaces — how far the red wash extends.
 * Range/Threat is the real targeting distance; absent those, a projected (Line/Cone
 * length) or area (Blast/Burst radius) value is a reasonable stand-in for "how far this
 * weapon projects". `0` when the weapon carries no usable range (no wash painted).
 */
export function primaryReach(range: WeaponRange[]): number {
  return maxVal(range, REACH_KINDS) || maxVal(range, PROJECT_KINDS) || maxVal(range, AREA_KINDS);
}

/**
 * The weapon's **THREAT** value — the reach a wielded weapon threatens for the purposes of
 * triggering reactions like Overwatch (RAW: "Threat: … If unspecified, this value is 1.").
 * Distinct from {@link primaryReach}: a Rifle (Range 10, no
 * Threat tag) threatens only 1 space, NOT its firing range — Threat is an explicit range
 * band a reach weapon (a spear, a Threat-2 polearm) carries, defaulting to **1** (melee
 * reach) when the weapon names none. Every weapon has SOME threat (never `0`/unknown, unlike
 * `primaryReach`'s "no range data ⇒ wall stands down" carve-out) — Overwatch is always live.
 */
export function threatValue(range: WeaponRange[]): number {
  const explicit = maxVal(range, ["threat"]);
  return explicit > 0 ? explicit : 1;
}

/** Whether a weapon's range projects an **area template** (Blast/Burst/Line/Cone) vs. a single target. */
export function isAreaRange(range: WeaponRange[]): boolean {
  return range.some((r) => r.kind === "blast" || r.kind === "burst" || r.kind === "line" || r.kind === "cone");
}

/**
 * Whether a weapon's frozen `range` marks it a **ranged** attack — the RAW discriminator the cover
 * math is gated on (Core p.66: cover applies to ranged attacks only, never melee/tech). Mirrors
 * COMP/CON's own `MechWeapon.Attack` getter: a weapon whose
 * **first** range entry is `"threat"` is melee (every compendium `type:"Melee"` weapon
 * freezes to exactly `[{kind:"threat",val:1}]` — verified against `@massif/lancer-data`,
 * no exceptions — and the resolver's synthetic melee-control-action profiles, Ram/Grapple/
 * Improvised Attack, freeze the identical shape). A weapon with **no** usable range data at
 * all (the unarmed-strike fallback) is likewise not ranged — range is *absent*, not
 * ranged-and-zero. Everything else (an explicit `"range"` entry, or a Line/Cone/Blast/Burst
 * weapon with no Threat entry — a Railgun's `[{kind:"line",val:20}]`) is ranged.
 *
 * Only answers the weapon-*shape* half of "is this attack subject to cover" — a tech attack
 * (`intent.tech`) is exempt by RAW regardless of the firing weapon's range, so callers must
 * check that separately (this function has no `intent` to read).
 */
export function isRangedAttack(range: WeaponRange[] | undefined): boolean {
  if (!range || range.length === 0) return false;
  return range[0]!.kind !== "threat";
}

/**
 * Is this attack ranged, `distance` spaces from its target? A weapon printed **Threat first with a
 * Range too** — the Engineer's prototype, `[Threat 1, Range 10]`, before its owner records a weapon
 * type (Core p.93: "either Threat 1 (melee) or Range 10 (all other types)") — is melee within its
 * Threat and ranged beyond it. `distance` `null` (no target in hand, or either side unplaced) reads
 * it as ranged, since its reach is its Range. Every other weapon answers {@link isRangedAttack}'s
 * shape: a CQB weapon printed `[Range 5, Threat 3]` stays ranged adjacent, as RAW has it.
 */
export function isRangedAttackAt(range: WeaponRange[] | undefined, distance: number | null): boolean {
  if (!range || range.length === 0) return false;
  const dual = range[0]!.kind === "threat" && range.some((r) => r.kind !== "threat");
  if (!dual) return isRangedAttack(range);
  return distance === null || distance > range[0]!.val;
}

/**
 * The faint-red **reach wash**: every cell within the weapon's {@link primaryReach} of the
 * attacker's footprint (Chebyshev disk unioned over each footprint cell, mirroring
 * `reachableCells`' footprint-union so a Size > 1 attacker's reach hugs its body). `"x,y"`
 * keys. `null` when the attacker is unplaced / off-square or the weapon has no range.
 */
export function attackReachCells(attacker: Combatant, range: WeaponRange[]): Set<string> | null {
  const anchor = squareAnchor(attacker);
  if (!anchor) return null;
  const reach = primaryReach(range);
  if (reach <= 0) return null;
  const cells = new Set<string>();
  for (const f of squareFootprint(anchor, attacker.base.size)) {
    for (const c of chebyshevDisk(f, reach)) cells.add(squareKey(c));
  }
  return cells;
}

/**
 * The bright-red **affected template** for a targeted cell: the cells a committed attack
 * would actually hit. Always includes the aimed cell, plus each range entry's area:
 *
 *   - **Blast / Burst N** — a Chebyshev disk of radius N centered on the targeted cell;
 *   - **Line N** — a one-wide ray of length N from the attacker in the aimed direction;
 *   - **Cone N** — the 90° wedge of length N from the attacker toward the targeted cell;
 *   - **Range / Threat** — no area beyond the target cell (the wash already shows reach).
 *
 * `"x,y"` keys. Line/Cone need the attacker's anchor as their apex; if it's unplaced they
 * contribute nothing (the bare target cell still highlights).
 */
export function attackAffectedCells(attacker: Combatant, target: Square, range: WeaponRange[]): Set<string> {
  const cells = new Set<string>([squareKey(target)]);
  const anchor = squareAnchor(attacker);
  for (const r of range) {
    switch (r.kind) {
      case "blast":
      case "burst":
        for (const c of chebyshevDisk(target, r.val)) cells.add(squareKey(c));
        break;
      case "line":
        if (anchor) for (const c of ray(anchor, target, r.val)) cells.add(squareKey(c));
        break;
      case "cone":
        if (anchor) for (const c of cone(anchor, target, r.val)) cells.add(squareKey(c));
        break;
      // "range" / "threat": targeting reach only — the aimed cell is already highlighted.
    }
  }
  return cells;
}

/**
 * Whether a placed combatant sits **within a reach wash** — any of its footprint cells is in
 * `reachCells` (the set {@link attackReachCells} returns). The UI-side twin of the server's
 * `checkAttackRange` (`packages/rules/.../economy.ts`): because the wash is the Chebyshev disk of
 * the weapon's reach unioned over the attacker's footprint, a target whose footprint the wash
 * doesn't touch is exactly a target whose footprint-to-footprint distance exceeds the reach — the
 * same verdict the authority reaches. So gating a player's target click on this keeps the COMBAT.app
 * PLAY affordance honest with the gate: the reach wash is a wall, not a hint. An unplaced /
 * off-square target contributes no footprint cell ⇒ not in reach.
 */
export function combatantInReach(target: Combatant, reachCells: Set<string>): boolean {
  const anchor = squareAnchor(target);
  if (!anchor) return false;
  for (const f of squareFootprint(anchor, target.base.size)) {
    if (reachCells.has(squareKey(f))) return true;
  }
  return false;
}

/**
 * The **LoS exclusion** `attackAffectedCombatants` and its grid-agnostic PORT twin
 * ({@link attackAffectedCombatantKeys}) both apply to an area template's caught candidates:
 * a character the template catches but the measuring center can't SEE isn't affected (RAW
 * p.64 — Blast/Burst "cover and line of sight ... calculated based on the center", not the
 * attacker). Mirrors the server's `resolveAreaAttack` fan-out filter
 * (`packages/rules/src/encounter/resolve/attack.ts`) EXACTLY: the SAME anchor (the aimed cell for
 * Blast, the attacker's own footprint for Burst/Line/Cone), the SAME
 * {@link terrainVisibility} read, the SAME
 * `tg_seeking`/`tg_arcing` exemption (both skip the check — RAW p.65, mirroring the initiation
 * gate's `checkAttackLoS` exemption). Only filters when the range is an AREA template
 * ({@link isAreaRange}) — a single-target weapon's hit set is ungated here; its LoS is the
 * initiation gate's job (`attackTargetLosBlocked`, `combatInspector.ts`), not this fan-out. A
 * candidate with no footprint (shouldn't happen — callers only pass placed candidates) fails
 * OPEN (stays visible), matching the engine's own can't-measure fail-open.
 */
function filterAreaLosVisible(args: {
  world: World<Cell>;
  state: EncounterState;
  attacker: Combatant;
  aim: Cell;
  range: WeaponRange[];
  tags?: { id: string; val?: number }[];
  candidates: CombatantId[];
}): CombatantId[] {
  const { world, state, attacker, aim, range, tags, candidates } = args;
  if (!isAreaRange(range)) return candidates;
  const isSeeking = tags?.some((t) => t.id === "tg_seeking") ?? false;
  const isArcing = tags?.some((t) => t.id === "tg_arcing") ?? false;
  if (isSeeking || isArcing) return candidates;
  const isBlast = range.some((r) => r.kind === "blast");
  const originFp: Cell[] = isBlast ? [aim] : attacker.pos ? world.footprint(attacker.pos, attacker.base.size).cells() : [];
  if (originFp.length === 0) return candidates;
  const visible: CombatantId[] = [];
  for (const id of candidates) {
    const target = state.combatants[id];
    const targetFp = target?.pos ? world.footprint(target.pos, target.base.size).cells() : [];
    if (targetFp.length === 0) {
      visible.push(id); // unplaced/no-footprint — can't measure, fail open (mirrors the engine fold).
      continue;
    }
    const view = terrainVisibility({ space: world, terrain: state.terrain, from: originFp, to: targetFp, edges: state.edges, combatants: state.combatants, excludeCoverFrom: id });
    if (view.visible) visible.push(id);
  }
  return visible;
}

/**
 * The combatants a committed attack at `target` would resolve against — every *placed*
 * combatant (other than the attacker) whose footprint intersects {@link attackAffectedCells}.
 * This is the bridge from a targeted cell to the multi-target resolution set: a single-target
 * weapon's template is just the aimed cell, so this returns its occupant (or none); an AoE
 * weapon's template fans out to everyone caught in the Blast/Line/Cone/Burst.
 *
 * Order is stable (state insertion order) so resolution + replay are deterministic. Returns
 * ids only; the resolver looks each up. The attacker is excluded (no self-hits from its own
 * Line/Cone/Burst origin). `tags` is the firing weapon's frozen tag refs — only consulted for
 * the `tg_seeking`/`tg_arcing` LoS exemption ({@link filterAreaLosVisible}); omit it and every
 * AREA template still LoS-filters (never seeking/arcing exempt) — pass it whenever the caller
 * knows the weapon.
 */
export function attackAffectedCombatants(args: {
  state: EncounterState;
  attacker: Combatant;
  target: Square;
  range: WeaponRange[];
  tags?: { id: string; val?: number }[];
}): CombatantId[] {
  const { state, attacker, target, range, tags } = args;
  const affected = attackAffectedCells(attacker, target, range);
  const hit: CombatantId[] = [];
  for (const [id, c] of Object.entries(state.combatants)) {
    if (id === attacker.id) continue;
    const anchor = squareAnchor(c);
    if (!anchor) continue;
    for (const f of squareFootprint(anchor, c.base.size)) {
      if (affected.has(squareKey(f))) {
        hit.push(id);
        break;
      }
    }
  }
  return filterAreaLosVisible({ world: worldFor(state), state, attacker, aim: squareCell(target.x, target.y), range, tags, candidates: hit });
}

// ── The grid-agnostic PORT twins ───────────────────────────────────────────────────
//
// The overlay reads the SAME `worldFor(state)` spatial port the resolver fans out over
// (`resolveAreaAttack` on `enumerateArea`), instead of the square-only `chebyshevDisk`/`ray`/`cone`
// above — so the on-canvas reach wash + AoE template + who's-hit dimming stay honest on hex as well
// as square. On the live 2d-square surface these produce the *identical* cell keys the square helpers
// above do (`world.disk`/`linePattern`/`conePattern` delegate to those very primitives, `world.key`
// == `squareKey`), so nothing changes for square play — the port is just the one geometry source now.

/** The footprint cell keys of a placed combatant on the encounter's grid (`[]` if unplaced). */
function footprintKeysOf(world: World<Cell>, c: Combatant): string[] {
  if (!c.pos) return [];
  return world.footprint(c.pos, c.base.size).cells().map((cell) => world.key(cell));
}

/**
 * The public form of {@link footprintKeysOf} — a placed combatant's own footprint cell keys on the
 * encounter's grid (`[]` if unplaced). For a caller (the COMBAT.app overlay) that needs a SPECIFIC
 * combatant's footprint keys directly, e.g. to mark those cells distinctly once it already knows
 * (via {@link attackAffectedCombatantKeys}) which combatant ids are LoS-excluded from an armed AoE.
 */
export function combatantFootprintKeys(state: EncounterState, c: Combatant): string[] {
  return footprintKeysOf(worldFor(state), c);
}

/** One cell of a reach wash, paired with its {@link ReachCell.key} for set-membership lookups. */
export type ReachCell = { key: string; cell: Cell };

/**
 * The **reach wash**, as actual cells (not just keys) — every cell within the weapon's
 * {@link primaryReach} of the attacker's footprint (a port `disk` unioned over each footprint cell),
 * on the encounter's grid. {@link attackReachKeys} is the bare-key twin built on top of this; callers
 * that need to run further per-cell geometry against the wash (e.g. a per-cell LoS query) take this
 * form instead of re-deriving cells from string keys. `null` when the attacker is unplaced or the
 * weapon has no usable reach.
 */
export function attackReachCellList(state: EncounterState, attacker: Combatant, range: WeaponRange[]): ReachCell[] | null {
  if (!attacker.pos) return null;
  const reach = primaryReach(range);
  if (reach <= 0) return null;
  const world = worldFor(state);
  const seen = new Set<string>();
  const out: ReachCell[] = [];
  for (const f of world.footprint(attacker.pos, attacker.base.size).cells()) {
    for (const c of world.disk(f, reach).cells()) {
      const key = world.key(c);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, cell: c });
    }
  }
  return out;
}

/**
 * The **reach wash** cell keys — the grid-agnostic twin of {@link attackReachCells}, built atop
 * {@link attackReachCellList}. `null` when the attacker is unplaced or the weapon has no usable reach.
 */
export function attackReachKeys(state: EncounterState, attacker: Combatant, range: WeaponRange[]): Set<string> | null {
  const list = attackReachCellList(state, attacker, range);
  return list === null ? null : new Set(list.map((r) => r.key));
}

/**
 * The **affected AoE template** cell keys for an aim — the aimed cell plus each range entry's area, on
 * the encounter's grid via the port (`disk` for Blast/Burst radial, `linePattern`/`conePattern` for the
 * directional shapes, aimed from the attacker toward `toward` — defaulting to the aimed cell). The
 * grid-agnostic twin of {@link attackAffectedCells}: what the resolver's `resolveAreaAttack` catches.
 */
export function attackAffectedKeys(args: {
  state: EncounterState;
  attacker: Combatant;
  aim: Cell;
  toward?: Cell;
  range: WeaponRange[];
}): Set<string> {
  const { state, attacker, aim, range } = args;
  const world = worldFor(state);
  const keys = new Set<string>([world.key(aim)]);
  const apex = attacker.pos;
  const dir = args.toward ?? aim;
  for (const r of range) {
    switch (r.kind) {
      case "blast":
      case "burst":
        for (const c of world.disk(aim, r.val).cells()) keys.add(world.key(c));
        break;
      case "line":
        if (apex) for (const c of world.linePattern(apex, dir, r.val).cells()) keys.add(world.key(c));
        break;
      case "cone":
        if (apex) for (const c of world.conePattern(apex, dir, r.val).cells()) keys.add(world.key(c));
        break;
      // "range" / "threat": targeting reach only — the aimed cell is already highlighted.
    }
  }
  return keys;
}

/**
 * The grid-agnostic twin of {@link attackAffectedCombatants} —
 * the TRUE hit set for an armed AoE aim, on the encounter's grid via {@link attackAffectedKeys}: every
 * placed combatant (other than the attacker) whose footprint intersects the template, minus whoever
 * the measuring center can't SEE ({@link filterAreaLosVisible} — the SAME anchor + LoS read +
 * `tg_seeking`/`tg_arcing` exemption the server's fan-out filter applies). This is what the COMBAT.app
 * red-template preview reads to know who the template will ACTUALLY resolve against — RAW doesn't
 * shrink the painted template itself (`attackAffectedKeys` is unchanged), only the per-target hit list.
 * `tags` is the armed weapon's frozen tag refs (optional — omit and every AREA template still
 * LoS-filters, never seeking/arcing exempt).
 */
export function attackAffectedCombatantKeys(args: {
  state: EncounterState;
  attacker: Combatant;
  aim: Cell;
  toward?: Cell;
  range: WeaponRange[];
  tags?: { id: string; val?: number }[];
  /**
   * The armed weapon doesn't attack the wielder's own side (the Veil Rifle's `sparesAllies`, read
   * off the projected `ProjectedEncounterState.sparingWeapons`): caught allies leave the hit set,
   * as the server's fan-out drops them.
   */
  sparesAllies?: boolean;
}): CombatantId[] {
  const { state, attacker, aim, toward, range, tags, sparesAllies } = args;
  const world = worldFor(state);
  const keys = attackAffectedKeys({ state, attacker, aim, toward, range });
  const candidates: CombatantId[] = [];
  for (const [id, c] of Object.entries(state.combatants)) {
    if (id === attacker.id) continue;
    if (sparesAllies === true && sideOf(c) === sideOf(attacker)) continue;
    if (footprintKeysOf(world, c).some((k) => keys.has(k))) candidates.push(id);
  }
  return filterAreaLosVisible({ world, state, attacker, aim, range, tags, candidates });
}

/**
 * Whether a placed combatant sits **within a reach-key set** — any of its footprint cells is in
 * `reachKeys` (a set from {@link attackReachKeys}), on the encounter's grid. The grid-agnostic twin
 * of {@link combatantInReach}. An unplaced target contributes no footprint cell ⇒ not in reach.
 */
export function combatantInReachKeys(state: EncounterState, target: Combatant, reachKeys: Set<string>): boolean {
  const world = worldFor(state);
  return footprintKeysOf(world, target).some((k) => reachKeys.has(k));
}
